import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check,
  X,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Loader2,
  ImageIcon,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CuratorView({ galleryId, photos = [], onPhotosUpdate }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [decisions, setDecisions] = useState({});
  const [saving, setSaving] = useState(false);

  const currentPhoto = photos[currentIndex];
  const acceptedCount = Object.values(decisions).filter(d => d === 'approved').length;
  const rejectedCount = Object.values(decisions).filter(d => d === 'rejected').length;
  const pendingCount = photos.length - acceptedCount - rejectedCount;

  useEffect(() => {
    // Initialize decisions from photo approval_status
    const initial = {};
    photos.forEach(p => {
      if (p.approval_status && p.approval_status !== 'draft') {
        initial[p.id] = p.approval_status;
      }
    });
    setDecisions(initial);
  }, [photos]);

  const handleDecision = useCallback((decision) => {
    if (!currentPhoto) return;

    setDecisions(prev => ({
      ...prev,
      [currentPhoto.id]: decision
    }));

    // Auto-advance to next undecided photo
    const nextUndecided = photos.findIndex((p, idx) =>
      idx > currentIndex && !decisions[p.id] && p.id !== currentPhoto.id
    );

    if (nextUndecided !== -1) {
      setCurrentIndex(nextUndecided);
    } else if (currentIndex < photos.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentPhoto, currentIndex, photos, decisions]);

  const handleReset = () => {
    if (!currentPhoto) return;
    setDecisions(prev => {
      const updated = { ...prev };
      delete updated[currentPhoto.id];
      return updated;
    });
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const updates = Object.entries(decisions).map(([photoId, status]) =>
        axios.put(`${API}/photos/${photoId}/approval`, { approval_status: status })
      );
      await Promise.all(updates);
      toast.success(`Zapisano ${Object.keys(decisions).length} decyzji`);
      if (onPhotosUpdate) onPhotosUpdate();
    } catch (error) {
      toast.error('Nie udalo sie zapisac decyzji');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = useCallback((e) => {
    switch (e.key) {
      case 'ArrowLeft':
        setCurrentIndex(prev => Math.max(0, prev - 1));
        break;
      case 'ArrowRight':
        setCurrentIndex(prev => Math.min(photos.length - 1, prev + 1));
        break;
      case 'a':
      case 'Enter':
        handleDecision('approved');
        break;
      case 'r':
      case 'Backspace':
        handleDecision('rejected');
        break;
      case 'u':
        handleReset();
        break;
      default:
        break;
    }
  }, [handleDecision, photos.length]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (photos.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-12 text-center">
          <ImageIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-medium mb-2">Brak zdjec do przegladania</h3>
          <p className="text-sm text-muted-foreground">
            Dodaj zdjecia do galerii, aby rozpoczac kuratorowanie
          </p>
        </CardContent>
      </Card>
    );
  }

  const currentDecision = currentPhoto ? decisions[currentPhoto.id] : null;

  return (
    <div className="space-y-4">
      {/* Stats Bar */}
      <div className="flex items-center justify-between p-4 bg-card border border-border rounded-lg">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-green-500/10 text-green-500">
              <CheckCircle className="w-3 h-3 mr-1" />
              {acceptedCount} zaakceptowanych
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-red-500/10 text-red-500">
              <XCircle className="w-3 h-3 mr-1" />
              {rejectedCount} odrzuconych
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">
              {pendingCount} oczekujacych
            </Badge>
          </div>
        </div>
        <Button
          onClick={handleSaveAll}
          disabled={saving || Object.keys(decisions).length === 0}
        >
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Zapisz wszystkie decyzje
        </Button>
      </div>

      {/* Main Curator Area */}
      <div className="relative bg-black rounded-lg overflow-hidden" style={{ height: '70vh' }}>
        {/* Navigation */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white"
          onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
          disabled={currentIndex === 0}
        >
          <ChevronLeft className="w-8 h-8" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white"
          onClick={() => setCurrentIndex(prev => Math.min(photos.length - 1, prev + 1))}
          disabled={currentIndex === photos.length - 1}
        >
          <ChevronRight className="w-8 h-8" />
        </Button>

        {/* Photo */}
        <AnimatePresence mode="wait">
          {currentPhoto && (
            <motion.div
              key={currentPhoto.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="w-full h-full flex items-center justify-center p-8"
            >
              <img
                src={currentPhoto.url || currentPhoto.thumbnail_url}
                alt={currentPhoto.original_filename}
                className="max-w-full max-h-full object-contain"
              />

              {/* Decision Overlay */}
              {currentDecision && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`absolute inset-0 pointer-events-none flex items-center justify-center ${
                    currentDecision === 'approved'
                      ? 'bg-green-500/20'
                      : 'bg-red-500/20'
                  }`}
                >
                  {currentDecision === 'approved' ? (
                    <CheckCircle className="w-32 h-32 text-green-500" />
                  ) : (
                    <XCircle className="w-32 h-32 text-red-500" />
                  )}
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Counter */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/70 px-4 py-2 rounded-full text-white text-sm">
          {currentIndex + 1} / {photos.length}
        </div>

        {/* Keyboard Hints */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 text-xs text-white/70">
          <span className="bg-white/20 px-2 py-1 rounded">A/Enter = Akceptuj</span>
          <span className="bg-white/20 px-2 py-1 rounded">R/Backspace = Odrzuc</span>
          <span className="bg-white/20 px-2 py-1 rounded">U = Cofnij</span>
          <span className="bg-white/20 px-2 py-1 rounded">Strzalki = Nawigacja</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-center gap-4">
        <Button
          variant="outline"
          size="lg"
          className="w-40 border-red-500/50 text-red-500 hover:bg-red-500/10"
          onClick={() => handleDecision('rejected')}
        >
          <X className="w-5 h-5 mr-2" />
          Odrzuc (R)
        </Button>
        <Button
          variant="outline"
          size="lg"
          className="w-32"
          onClick={handleReset}
          disabled={!currentDecision}
        >
          <RotateCcw className="w-5 h-5 mr-2" />
          Cofnij (U)
        </Button>
        <Button
          variant="outline"
          size="lg"
          className="w-40 border-green-500/50 text-green-500 hover:bg-green-500/10"
          onClick={() => handleDecision('approved')}
        >
          <Check className="w-5 h-5 mr-2" />
          Akceptuj (A)
        </Button>
      </div>
    </div>
  );
}
